import { Alert, Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import type { IWorkbookInspection, IWorkbookInspectionResponse } from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { buildWorkbookInspectionPlainEnglishSummary } from "./workbookInspectionPlainEnglish";
import { getDisplayedInspectionWarnings, getDisplayedWarningCount, formatWorksheetVisibility } from "./workbookInspectionDisplay";

export interface WorkbookInspectionSummaryProps {
    inspectionResponse: IWorkbookInspectionResponse | null;
    loading: boolean;
    refreshing: boolean;
    error: string | null;
    hasActiveTemplate: boolean;
    onRefresh: () => void;
}

export function WorkbookInspectionSummary({
    inspectionResponse,
    loading,
    refreshing,
    error,
    hasActiveTemplate,
    onRefresh,
}: WorkbookInspectionSummaryProps) {
    if (!hasActiveTemplate) {
        return (
            <Card className="mb-4">
                <Card.Header>Workbook Inspection</Card.Header>
                <Card.Body>
                    <Alert variant="info" className="mb-0">
                        Upload a template to inspect workbook structure and dropdown sources.
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card className="mb-4">
                <Card.Body className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Loading workbook inspection…</span>
                </Card.Body>
            </Card>
        );
    }

    const inspection: IWorkbookInspection | undefined = inspectionResponse?.inspection;

    return (
        <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Workbook Inspection</span>
                <Button variant="outline-secondary" size="sm" onClick={onRefresh} disabled={refreshing}>
                    {refreshing ? "Refreshing…" : "Refresh inspection"}
                </Button>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                {!inspection ? (
                    <Alert variant="warning" className="mb-0">
                        Workbook inspection is not available. Run inspection after uploading a valid template.
                    </Alert>
                ) : (
                    <>
                        {(() => {
                            const summary = buildWorkbookInspectionPlainEnglishSummary(inspection);
                            const displayedWarnings = getDisplayedInspectionWarnings(inspection);
                            const displayedWarningCount = getDisplayedWarningCount(inspection);

                            return (
                                <>
                                    <p className="mb-3">
                                        This workbook has <strong>{summary.worksheetCount}</strong> worksheets,{" "}
                                        <strong>{summary.namedRangeCount}</strong> named ranges, and{" "}
                                        <strong>{summary.dataValidationCount}</strong> data-validation rules. That is enough to configure the import mapping.
                                    </p>

                                    {summary.protectionSummaryText && (
                                        <Alert variant="info" className="py-2 small">
                                            {summary.protectionSummaryText}
                                        </Alert>
                                    )}

                                    {summary.hiddenSheetsSummaryText && (
                                        <Alert variant="info" className="py-2 small">
                                            {summary.hiddenSheetsSummaryText}
                                        </Alert>
                                    )}

                                    {summary.safeToIgnoreNamedRanges.length > 0 && (
                                        <Alert variant="secondary" className="py-2 small">
                                            <strong>Safe to ignore ({summary.safeToIgnoreNamedRanges.length} broken/external named ranges):</strong>{" "}
                                            {summary.safeToIgnoreNamedRanges.map((range) => range.name).join(", ")}
                                        </Alert>
                                    )}

                                    {summary.reviewWarnings.length > 0 && (
                                        <Alert variant="warning" className="py-2 small">
                                            <strong>Review these warnings:</strong>
                                            <ul className="mb-0 mt-2">
                                                {summary.reviewWarnings.map((warning, index) => (
                                                    <li key={index}>{warning}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}

                                    <Row className="g-3 mb-3">
                                        <Col md={3}>
                                            <div className="text-muted small">Worksheets</div>
                                            <div className="fs-5">{inspection.workbook.worksheetCount}</div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-muted small">Named ranges</div>
                                            <div className="fs-5">{inspection.workbook.namedRangeCount}</div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-muted small">Data validations</div>
                                            <div className="fs-5">{inspection.workbook.dataValidationCount}</div>
                                        </Col>
                                        <Col md={3}>
                                            <div className="text-muted small">Warnings</div>
                                            <div className="fs-6">
                                                {displayedWarningCount > 0 ? (
                                                    <Badge bg="warning">{displayedWarningCount}</Badge>
                                                ) : (
                                                    <span className="text-muted">No warnings to review</span>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    {inspectionResponse?.fromCache && (
                                        <Alert variant="secondary" className="py-2 small">
                                            Showing cached inspection from {new Date(inspection.inspectedAtUtc).toLocaleString()}.
                                        </Alert>
                                    )}

                                    <h3 className="h6">Worksheets</h3>
                                    <Table responsive size="sm" className="mb-4">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th className="text-center">Visibility</th>
                                                <th className="text-center">Protected</th>
                                                <th>Used range</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inspection.worksheets.map((sheet) => (
                                                <tr key={sheet.name}>
                                                    <td>{sheet.name}</td>
                                                    <td className="text-center">{formatWorksheetVisibility(sheet.visibility)}</td>
                                                    <td className="text-center">{sheet.isProtected ? "Yes" : "No"}</td>
                                                    <td>{sheet.usedRange || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {displayedWarnings.length > 0 && (
                                        <>
                                            <h3 className="h6">Inspection warnings</h3>
                                            <ul className="mb-0">
                                                {displayedWarnings.map((warning, index) => (
                                                    <li key={`${warning.code}-${warning.worksheet ?? "workbook"}-${index}`}>
                                                        <strong>{warning.code}</strong>
                                                        {warning.worksheet ? ` (${warning.worksheet})` : ""}: {warning.message}
                                                        {warning.detail ? ` — ${warning.detail}` : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                    </>
                )}
            </Card.Body>
        </Card>
    );
}
